import { Category } from "@/dto/category";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { OrderType } from "@/types";
import { giftCardCategory } from "@/utils/giftCardUtils";
import pom from "@/utils/pom";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { applyDataTestId } from "@/utils/testUtils";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Dispatch, SetStateAction, useMemo } from "react";
import { MobileDayPartMenuList } from "./dayPartMenuSelector";
import DefaultLogo from "@/components/header/defaultLogo.svg";

export interface MobileNavigationBarProps {
  sideMenuOpen: boolean;
  setSideMenuOpen: Dispatch<SetStateAction<boolean>>;
  categories: Category[];
  scanToOrderInfo: ScanToOrderInfo;
  merchant: Merchant;
  dayPartMenus?: DayPart[];
  selectedMenu?: DayPart | null;
  // eslint-disable-next-line no-unused-vars
  onDayPartMenuChange?: (menu: DayPart) => void;
}

export const MobileNavigationBar = ({
  sideMenuOpen,
  setSideMenuOpen,
  categories,
  scanToOrderInfo,
  merchant,
  dayPartMenus,
  selectedMenu = null,
  onDayPartMenuChange,
}: MobileNavigationBarProps) => {
  const t = useTranslations("MobileNavigationBar");
  const {
    isService,
    isRestaurant,
    merchantMeta,
    isHybrid,
    isSellingGiftCards,
    logo = DefaultLogo.src,
  } = merchant;
  const searchParams = useSearchParams();
  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const fileteredDayPartMenus: DayPart[] = useMemo(() => {
    if (!isDineIn) return dayPartMenus ?? [];

    if (isDineIn && selectedMenu) return [selectedMenu];

    return [];
  }, [isDineIn, dayPartMenus, selectedMenu]);

  return (
    <div>
      <Dialog
        open={sideMenuOpen}
        onClose={setSideMenuOpen}
        className="relative py-10 z-50"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 flex w-[84%]">
              <DialogPanel
                transition
                className="pointer-events-auto relative w-screen max-w-md transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
              >
                <TransitionChild>
                  <div className="absolute right-[27px] top-[21px] z-10 -ml-8 flex duration-300 ease-in-out data-[closed]:opacity-0">
                    <button
                      type="button"
                      onClick={() => setSideMenuOpen(false)}
                      className="relative rounded-md text-black"
                    >
                      <span className="absolute -inset-2.5" />
                      <span className="sr-only">{t("closePanel")}</span>
                      <XMarkIcon aria-hidden="true" className="size-6" />
                    </button>
                  </div>
                </TransitionChild>
                <div className="relative flex h-full flex-col overflow-y-auto pb-10 pt-10 px-5 bg-white shadow-xl">
                  <DialogTitle>
                    <Link href={"/"} onClick={() => setSideMenuOpen(false)}>
                      <Image
                        data-testid={applyDataTestId(
                          pom.header.merchantLogoDataTestId
                        )}
                        src={logo}
                        width={51}
                        height={51}
                        alt={pom.header.merchantLogo}
                      />
                    </Link>
                  </DialogTitle>
                  <div className="relative mt-6 flex flex-col gap-6 flex-1">
                    {isService ||
                    (isRestaurant && dayPartMenus?.length == 0) ? (
                      <>
                        {isService && !merchant.hasOnlyRetailItems && (
                          <Link
                            href="/services/all"
                            className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                            onClick={() => setSideMenuOpen(false)}
                          >
                            {t("services")}
                          </Link>
                        )}
                        {(isHybrid ||
                          (isService && merchant?.hasOnlyRetailItems)) && (
                          <Link
                            href="/shop"
                            className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                            onClick={() => setSideMenuOpen(false)}
                          >
                            {t("shop")}
                          </Link>
                        )}
                        {isRestaurant && (
                          <Link
                            href="/menu/all"
                            className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                            onClick={() => setSideMenuOpen(false)}
                          >
                            {t("menu")}
                          </Link>
                        )}
                      </>
                    ) : dayPartMenus && dayPartMenus?.length > 0 ? (
                      <MobileDayPartMenuList
                        dayPartMenus={fileteredDayPartMenus}
                        merchant={merchant}
                        onMenuSelect={(menu) => {
                          setSideMenuOpen(false);
                          onDayPartMenuChange?.(menu);
                        }}
                        selectedMenu={selectedMenu}
                      />
                    ) : (
                      categories
                        .filter((c: Category) => c.id !== giftCardCategory.id)
                        .map((c: { id: string; name: string }) => {
                          let queryParams = "";
                          if (scanToOrderInfo) {
                            queryParams =
                              buildScanToOrderSearchParams(scanToOrderInfo);
                          }
                          const restaurantUrl = !queryParams
                            ? `/menu/all#${encodeURIComponent(c.name)}`
                            : `/menu/all/${queryParams}#${encodeURIComponent(
                                c.name
                              )}`;
                          return (
                            <Link
                              key={c.id}
                              href={
                                isService
                                  ? `/services/all#${encodeURIComponent(
                                      c.name
                                    )}`
                                  : isRestaurant
                                  ? restaurantUrl
                                  : `/${encodeURIComponent(c.name)}`
                              }
                              className={[
                                "flex",
                                "text-lg",
                                "text-[#2E2E2E]",
                              ].join(" ")}
                              onClick={() => setSideMenuOpen(false)}
                            >
                              {c.name}
                            </Link>
                          );
                        })
                    )}

                    {isSellingGiftCards && !scanToOrderInfo && (
                      <Link
                        href="/giftcard"
                        className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                        onClick={() => setSideMenuOpen(false)}
                      >
                        {t("giftCard")}
                      </Link>
                    )}
                    {merchantMeta?.selectedSetup !== "Simple" && (
                      <Link
                        href="/about"
                        className={["text-lg", "text-[#2E2E2E]"].join(" ")}
                        onClick={() => setSideMenuOpen(false)}
                      >
                        {t("about")}
                      </Link>
                    )}
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
